<template>
  <div class="graph-container">
    <canvas
      class="line-chart"
      height="700"
      ref="chart">
    </canvas>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Chart from 'chart.js';
import bitToKbps from '@/utils/unitConverter';

export default {
  data() {
    return {
      chart: null,
      time: [],
      video: [],
      audio: [],
      minAudioBitrate: 999999,
      minVideoBitrate: 999999,
      maxAudioBitrate: 0,
      maxVideoBitrate: 0,
    };
  },
  computed: {
    ...mapGetters([
      'videoBitrateObject',
      'audioBitrateObject',
      'separateAudioBitrate',
    ]),
    isMobileDevice() {
      const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      return viewportWidth <= 540;
    },
    hasAudio() {
      return this.audioBitrateObject;
    },
    hasVideo() {
      return this.videoBitrateObject;
    },
    interval() {
      return this.$store.state.chartInterval;
    },
  },
  watch: {
    videoBitrateObject() {
      setTimeout(() => {
        if (!this.hasVideo && !this.hasAudio) {
          this.$refs.chart.style.height = '0';
          this.$refs.chart.style.minHeight = '0';
        } else {
          this.$refs.chart.style.height = 'auto';
          this.$refs.chart.style.minHeight = 'auto';
        }
      }, 300);
      if (this.videoBitrateObject !== undefined) {
        if (!this.chart) {
          this.drawChart();
        } else {
          this.updateChart();
        }
      }
    },
    interval() {
      this.updateChart();
    },
    separateAudioBitrate() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
        this.drawChart();
      }
    },
  },
  created() {
    setTimeout(() => this.drawChart(), 300);
  },
  methods: {
    drawChart() {
      this.convertValuesUnit();
      const videoLabel = this.separateAudioBitrate ? 'Video Bitrate (Kbps)' : 'Stream Bitrate (Kbps)';
      const videoAxisLabel = this.separateAudioBitrate ? 'Video Bitrate' : 'Stream Bitrate';
      const datasets = [
        {
          label: videoLabel,
          fontColor: '#FFF',
          yAxisID: 'video',
          data: this.video,
          borderColor: '#55c6ef',
          fill: false,
          lineTension: 0.1,
          pointRadius: 2,
          pointHoverRadius: 6,
        },
      ];
      if (this.separateAudioBitrate) {
        datasets.push({
          label: 'Audio Bitrate (Kbps)',
          yAxisID: 'audio',
          data: this.audio,
          borderColor: '#9d61a9',
          fill: false,
          lineTension: 0,
          pointRadius: 2,
          pointHoverRadius: 6,
        });
      }
      const yAxes = [
        {
          id: 'video',
          type: 'linear',
          position: 'left',
          ticks: {
            fontColor: '#55c6ef',
            fontSize: 14,
            max: this.getVideoBitrateMaxTick(),
            min: 0,
            fontStyle: 'bold',
          },
          scaleLabel: {
            display: true,
            labelString: videoAxisLabel,
            fontColor: '#55c6ef',
          },
        },
      ];
      if (this.separateAudioBitrate) {
        yAxes.push({
          id: 'audio',
          type: 'linear',
          position: 'right',
          ticks: {
            fontColor: '#e28ff3',
            fontSize: 14,
            fontStyle: 'bold',
            max: this.getAudioBitrateMaxTick(),
            min: 0,
            stepSize: 32,
          },
          scaleLabel: {
            display: true,
            labelString: 'Audio Bitrate',
            fontColor: '#e28ff3',
          },
        });
      }
      const showSeparateAudio = this.separateAudioBitrate;
      this.chart = new Chart(this.$refs.chart, {
        type: 'line',
        data: {
          labels: this.time,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          bezierCurve: false,
          scales: {
            yAxes,
            xAxes: [
              {
                type: 'time',
                ticks: {
                  fontColor: '#FFF',
                },
                time: {
                  unit: 'minute',
                  displayFormats: {
                    minute: 'HH:mm',
                  },
                },
              },
            ],
          },
          legend: {
            display: true,
            labels: {
              fontColor: '#FFF',
            },
          },
          onResize() {
            const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            if (viewportWidth < 540) {
              this.elements.line.borderWidth = 2;
              this.legend.labels.padding = 5;
              this.legend.position = 'bottom';
              this.scales.xAxes[0].ticks.fontSize = 7;
              this.scales.yAxes[0].ticks.fontSize = 10;
              this.scales.yAxes[0].scaleLabel.display = false;
              if (showSeparateAudio) {
                this.scales.yAxes[1].ticks.fontSize = 10;
                this.scales.yAxes[1].scaleLabel.display = false;
              }
            } else {
              this.elements.line.borderWidth = 3;
              this.legend.labels.padding = 10;
              this.legend.position = 'top';
              this.scales.xAxes[0].ticks.fontSize = 12;
              this.scales.yAxes[0].ticks.fontSize = 14;
              this.scales.yAxes[0].scaleLabel.display = true;
              if (showSeparateAudio) {
                this.scales.yAxes[1].ticks.fontSize = 14;
                this.scales.yAxes[1].scaleLabel.display = true;
              }
            }
          },
        },
      });
      if (this.isMobileDevice) {
        this.chart.options.onResize();
        this.chart.update({ duration: 0 });
      }
    },
    updateChart() {
      this.convertValuesUnit();
      this.chart.data.labels = this.time;
      this.chart.data.datasets[0].data = this.video;
      if (this.separateAudioBitrate && this.chart.data.datasets.length > 1) {
        this.chart.data.datasets[1].data = this.audio;
      }
      this.chart.update({ duration: 0 });
    },
    convertValuesUnit() {
      let time = [];
      let video = [];
      let audio = [];
      Object.entries(this.videoBitrateObject).forEach(([key]) => {
        time.push(this.getTime(key));
      });
      Object.entries(this.videoBitrateObject).forEach(([, bitrate]) => {
        const videoKbps = bitToKbps(bitrate);
        if (videoKbps > this.maxVideoBitrate) {
          this.maxVideoBitrate = videoKbps;
        }
        if (videoKbps < this.minVideoBitrate) {
          this.minVideoBitrate = videoKbps;
        }
        video.push(videoKbps);
      });
      if (this.separateAudioBitrate) {
        Object.entries(this.audioBitrateObject).forEach(([, bitrate]) => {
          const audioKbps = bitToKbps(bitrate);
          if (audioKbps > this.maxAudioBitrate) {
            this.maxAudioBitrate = audioKbps;
          }
          if (audioKbps < this.minAudioBitrate) {
            this.minAudioBitrate = audioKbps;
          }
          audio.push(audioKbps);
        });
      }

      const videoAvg = video.reduce((a, b) => a + b) / video.length;
      const audioAvg = this.separateAudioBitrate && audio.length ?
        audio.reduce((a, b) => a + b) / audio.length : 0;

      const currentData = {
        videoBitrate: {
          max: Math.floor(this.maxVideoBitrate),
          avg: Math.floor(videoAvg),
          min: Math.floor(this.minVideoBitrate),
        },
        audioBitrate: {
          max: this.separateAudioBitrate ? Math.floor(this.maxAudioBitrate) : 0,
          avg: Math.floor(audioAvg),
          min: this.separateAudioBitrate ? Math.floor(this.minAudioBitrate) : 0,
        },
      };

      this.$store.dispatch('setCurrentData', currentData);

      if (this.interval > 0) {
        time = time.slice(this.interval);
        audio = audio.slice(this.interval);
        video = video.slice(this.interval);
      }
      this.time = time;
      this.audio = audio;
      this.video = video;
    },
    getTime(unixTime) {
      return new Date(unixTime * 1000);
    },
    getAudioBitrateMaxTick() {
      if (this.maxAudioBitrate <= 96) {
        return 160;
      }
      if (this.maxAudioBitrate <= 128) {
        return 192;
      }
      if (this.maxAudioBitrate <= 160) {
        return 256;
      }
      if (this.maxAudioBitrate <= 192) {
        return 320;
      }
      if (this.maxAudioBitrate <= 320) {
        return 384;
      }
      if (this.maxAudioBitrate <= 384) {
        return 480;
      }
      if (this.maxAudioBitrate <= 480) {
        return 992;
      }
      return 1141;
    },
    getVideoBitrateMaxTick() {
      const max = Math.floor(this.maxVideoBitrate);
      if (max <= 5000) {
        return 7000;
      }
      if (max <= 7000) {
        return 9000;
      }
      if (max <= 8000) {
        return 10000;
      }
      if (max < 13000) {
        return 15000;
      }
      if (max < 18000) {
        return 20000;
      }
      return (max + 5000) - ((max + 5000) % 5000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/variables.scss";

.graph-container {
  width: 100%;
  text-align: center;
  margin-bottom: $spacer;
  .line-chart {
    width: 100% !important;
    height: 700px;
    min-height: 700px;
    max-width: 100%;
  }
}

@media screen and (max-width: 1660px) {
  .graph-container {
    .line-chart {
      width: 100%;
      height: 600px !important;
      min-height: 600px;
    }
  }
}

@media screen and (max-width: 1440px) {
  .graph-container {
    .line-chart {
      width: 100%;
      height: 620px !important;
      min-height: 620px;
    }
  }
}

@media screen and (max-width: 1440px) {
  .graph-container {
    .line-chart {
      width: 100%;
      height: 580px !important;
      min-height: 580px;
    }
  }
}

@media screen and (max-width: 1070px) {
  .graph-container {
    .line-chart {
      height: 510px !important;
      min-height: 510px;
    }
  }
}

@media screen and (max-width: 992px) {
  .graph-container {
    .line-chart {
      height: 480px !important;
      min-height: 480px;
    }
  }
}

@media screen and (max-width: 660px) {
  .graph-container {
    .line-chart {
      height: 540px !important;
      min-height: 540px;
    }
  }
}

@media screen and (max-width: 540px) {
  .graph-container {
    .line-chart {
      height: 340px !important;
      min-height: 340px;
    }
  }
}

@media screen and (max-width: 480px) {
  .graph-container {
    .line-chart {
      height: 280px !important;
      min-height: 280px;
    }
  }
}
</style>
